type CacheEntry = {
  data: unknown;
  timestamp: number;
};

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL = 30_000; // 30 seconds

export const problemUrl = (problemId: string) =>
  `/api/proxy/question-bank/problems/${encodeURIComponent(problemId)}`;

export const skillProblemsUrl = (skillId: string) =>
  `/api/proxy/question-bank/skills/${encodeURIComponent(skillId)}/problems`;

/** With no query these are the cache-key prefixes for every page/filter. */
export const skillsUrl = (query: URLSearchParams | string = "") =>
  `/api/proxy/question-bank/skills?${query}`;

export const coverageUrl = (query = "") =>
  `/api/proxy/question-bank/coverage?${query}`;

export async function qbFetch<T = unknown>(
  url: string,
  options?: RequestInit & { ttl?: number; skipCache?: boolean },
): Promise<T> {
  const { ttl = DEFAULT_TTL, skipCache = false, ...fetchOpts } = options ?? {};
  const method = (fetchOpts.method ?? "GET").toUpperCase();

  if (method === "GET" && !skipCache) {
    const cached = cache.get(url);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data as T;
    }
  }

  const res = await fetch(url, fetchOpts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }

  const data: T = await res.json();

  if (method === "GET") {
    cache.set(url, { data, timestamp: Date.now() });
  }

  return data;
}

export function invalidateQBCache(prefix?: string) {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

/** Every cached response a delete or restore of this problem makes stale. */
export function invalidateProblemCaches(problemId: string, skillId: string) {
  for (const prefix of [skillProblemsUrl(skillId), skillsUrl(), coverageUrl(), problemUrl(problemId)]) {
    invalidateQBCache(prefix);
  }
}
